import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactConfetti from "react-confetti";
import html2canvas from "html2canvas";
import {
  ArrowLeft, Loader2, Gift, Info, Download, Share2, Flame, Phone, Lock, Sparkles, Ticket,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { AppTheme } from "@/constants/theme";
import { analyticsService } from "@/services/analytics";
import { scratchCardService } from "@/services/scratchCardService";
import { depositService } from "@/services/depositService";
import { useAuth } from "@/hooks/useAuth";

type GamePhase = "entering" | "idle" | "scratching" | "revealed";

const SCRATCH_THRESHOLD = 0.8;
const BRUSH_RADIUS = 34;

const WIN_MESSAGES = ["BRAVO !", "GAGNÉ !", "FÉLICITATIONS !"];
const LOSE_EMOJIS = ["😢", "😞", "😔"];
const LOSE_LABELS = ["Pas de chance...", "Essayez encore !", "Presque !"];

// Stable sparkles
const SPARKLES = Array.from({ length: 18 }, (_, i) => ({
  id: i, x: 6 + ((i * 41 + 11) % 88), y: 10 + ((i * 59 + 7) % 80),
  size: 2 + (i % 3), opacity: 0.1 + (i % 5) * 0.05,
}));

// Stable symbols for card background pattern
const CARD_SYMBOLS = ["⭐", "💎", "🪙", "💰", "🎯", "🎰", "🃏", "⭐", "💎", "🪙", "💰", "🎯", "🎰", "🃏", "⭐", "💎"];

function createScratchBuffer(ctx: AudioContext): AudioBuffer {
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * 0.07);
  const buf = ctx.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / len;
    d[i] = ((Math.random() * 2 - 1) * 0.4 + Math.sin(i * 0.015) * 0.2 + Math.sin(i * 0.07) * 0.12) * (1 - t);
  }
  return buf;
}

export function ScratchCardPage() {
  const navigate = useNavigate();
  useAuth();
  const [phase, setPhase] = useState<GamePhase>("entering");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [played, setPlayed] = useState(false);
  const [depositCount, setDepositCount] = useState(0);

  const [won, setWon] = useState(false);
  const [prize, setPrize] = useState(0);
  const [refId, setRefId] = useState("");
  const [winModal, setWinModal] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [scratchPct, setScratchPct] = useState(0);

  // Pick stable emoji + label from init time
  const loseEmojiIdx = useRef(Math.floor(Math.random() * LOSE_EMOJIS.length));
  const winMsgIdx = useRef(Math.floor(Math.random() * WIN_MESSAGES.length));

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const receiptRef = useRef<HTMLDivElement>(null);
  const gameIdRef = useRef("");
  const isRevealedRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const canvasReadyRef = useRef(false);
  const scratchBufRef = useRef<AudioBuffer | null>(null);
  const revealFnRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return audioCtxRef.current;
  }, []);

  const playTone = useCallback((freq: number, dur: number, type: OscillatorType = "sine", vol = 0.3) => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = type; o.frequency.setValueAtTime(freq, ctx.currentTime);
      g.gain.setValueAtTime(vol, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + dur);
    } catch {}
  }, [getAudioCtx]);

  const sfxScratch = useCallback(() => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === "suspended") ctx.resume();
      if (!scratchBufRef.current) scratchBufRef.current = createScratchBuffer(ctx);
      const src = ctx.createBufferSource(); src.buffer = scratchBufRef.current;
      const flt = ctx.createBiquadFilter(); flt.type = "bandpass"; flt.frequency.value = 2500 + Math.random() * 3000; flt.Q.value = 0.6;
      const g = ctx.createGain(); g.gain.setValueAtTime(0.18, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.07);
      src.connect(flt); flt.connect(g); g.connect(ctx.destination); src.start();
    } catch {}
  }, [getAudioCtx]);

  const sfxWin = useCallback(() => {
    [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playTone(f, 0.35, "sine", 0.25), i * 120));
  }, [playTone]);

  useEffect(() => {
    if (document.getElementById("scratch-kf")) return;
    const s = document.createElement("style"); s.id = "scratch-kf";
    s.textContent = `
      @keyframes glow { 0%,100%{box-shadow:0 0 20px rgba(255,215,0,0.3)}50%{box-shadow:0 0 40px rgba(255,215,0,0.6)} }
    `;
    document.head.appendChild(s);
  }, []);

  // ─── Canvas ────────────────────────────────────────────────────
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const w = rect.width; const h = rect.height;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr; canvas.height = h * dpr;
    canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Silver scratch coating
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#6e6e6e"); grad.addColorStop(0.2, "#a0a0a0");
    grad.addColorStop(0.4, "#c8c8c8"); grad.addColorStop(0.5, "#b8b8b8");
    grad.addColorStop(0.6, "#c8c8c8"); grad.addColorStop(0.8, "#a0a0a0");
    grad.addColorStop(1, "#6e6e6e");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h);

    // Cross-hatch
    ctx.strokeStyle = "rgba(255,255,255,0.07)"; ctx.lineWidth = 1;
    for (let x = -h; x < w + h; x += 8) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + h, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x + h, 0); ctx.lineTo(x, h); ctx.stroke();
    }

    // Speckle noise
    for (let i = 0; i < 4000; i++) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.06})`;
      ctx.fillRect(Math.random() * w, Math.random() * h, 1, 1);
    }

    // Label
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = "bold 20px system-ui, sans-serif";
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fillText("GRATTEZ ICI", w / 2, h / 2 - 12);
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.fillText("Grattez pour découvrir votre prix", w / 2, h / 2 + 12);

    canvasReadyRef.current = true;
    isRevealedRef.current = false;
    lastPosRef.current = null;
  }, []);

  // ─── Brush ───────────────────────────────────────────────
  const doScratchAt = useCallback((ctx: CanvasRenderingContext2D, x: number, y: number) => {
    ctx.globalCompositeOperation = "destination-out";
    const r = BRUSH_RADIUS + Math.random() * 10;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    for (let i = 0; i < 5; i++) {
      const a = Math.random() * Math.PI * 2;
      ctx.beginPath(); ctx.arc(x + Math.cos(a) * r * (0.5 + Math.random() * 0.6), y + Math.sin(a) * r * (0.5 + Math.random() * 0.6), 3 + Math.random() * 10, 0, Math.PI * 2); ctx.fill();
    }
    for (let i = 0; i < 3; i++) {
      const a = Math.random() * Math.PI * 2;
      ctx.lineWidth = 1 + Math.random() * 2;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * r * (0.4 + Math.random()), y + Math.sin(a) * r * (0.4 + Math.random())); ctx.stroke();
    }
  }, []);

  const progressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkProgress = useCallback(() => {
    if (progressTimerRef.current) return;
    progressTimerRef.current = setTimeout(() => {
      progressTimerRef.current = null;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let clear = 0; const step = 32;
      for (let i = 3; i < img.data.length; i += step) { if (img.data[i] === 0) clear++; }
      setScratchPct(Math.min(clear / (img.data.length / step), 1));
      if (clear / (img.data.length / step) >= SCRATCH_THRESHOLD && !isRevealedRef.current) revealFnRef.current();
    }, 200);
  }, []);

  let sfxCount = 0;
  const scratch = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas || phase !== "scratching" || isRevealedRef.current || !canvasReadyRef.current) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.save(); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const last = lastPosRef.current;
    if (last) {
      const dx = x - last.x; const dy = y - last.y;
      const steps = Math.max(1, Math.floor(Math.sqrt(dx * dx + dy * dy) / 10));
      for (let i = 0; i <= steps; i++) { const t = i / steps; doScratchAt(ctx, last.x + dx * t, last.y + dy * t); }
    } else { doScratchAt(ctx, x, y); }
    lastPosRef.current = { x, y }; ctx.restore();
    sfxCount++;
    if (sfxCount % 4 === 0) sfxScratch();
    checkProgress();
  }, [phase, sfxScratch, doScratchAt, checkProgress]);

  const getPos = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current; if (!canvas) return { x: 0, y: 0 };
    const r = canvas.getBoundingClientRect();
    if ("touches" in e && e.touches.length > 0) return { x: e.touches[0].clientX - r.left, y: e.touches[0].clientY - r.top };
    return { x: (e as React.MouseEvent).clientX - r.left, y: (e as React.MouseEvent).clientY - r.top };
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => { e.preventDefault(); const p = getPos(e); scratch(p.x, p.y); }, [getPos, scratch]);
  const onTouchEnd = useCallback(() => { lastPosRef.current = null; }, []);
  const onMouseMove = useCallback((e: React.MouseEvent) => { if (!(e.buttons & 1)) { lastPosRef.current = null; return; } const p = getPos(e); scratch(p.x, p.y); }, [getPos, scratch]);

  // ─── Phone submit ──────────────────────────────────────────────
  const handleSubmitPhone = useCallback(async () => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length < 8) { setError("Entrez un numéro valide"); return; }
    setError(""); setLoading(true);
    try {
      const result = await depositService.fetchDepositHistoryForPaymentNumber(cleaned);
      if (result?.success && result.depots) {
        const today = new Date().toISOString().slice(0, 10);
        const td = result.depots.filter((d) => d.date_created && d.date_created.slice(0, 10) === today && (d.montant || 0) >= 250);
        setDepositCount(td.length);
        if (td.length === 0) { setError("Aucun dépôt Orange d'au moins 250 FCFA effectué aujourd'hui"); setLoading(false); return; }
        const already = parseInt(localStorage.getItem(`scratch_played_${today}`) || "0", 10);
        if (already >= td.length) setPlayed(true);
      } else { setError("Impossible de vérifier vos dépôts"); setLoading(false); return; }
      setPhone(cleaned); setPhase("idle");
    } catch { setError("Erreur de vérification"); } finally { setLoading(false); }
  }, [phone]);

  // ─── Start game ─────────────────────────────────────────────────
  const handlePlay = useCallback(async () => {
    if (played) { setError("Essai épuisé — Rechargez pour rejouer !"); return; }
    setError(""); setLoading(true);
    try {
      const r = await scratchCardService.init(phone);
      gameIdRef.current = r.gameId;
      setWon(r.won);
      setPrize(r.prize);
      setScratchPct(0);
      canvasReadyRef.current = false;
      isRevealedRef.current = false;
      setRefId(""); setWinModal(false); setConfetti(false);
      setPhase("scratching");
      setTimeout(() => initCanvas(), 80);
      analyticsService.trackEvent("scratch_card_init", { won: r.won, prize: r.prize, phone });
    } catch (e: any) {
      const msg = e.message || "Erreur lors du démarrage";
      setError(msg);
      if (msg.includes("Limite") || msg.includes("limite")) setPlayed(true);
    } finally { setLoading(false); }
  }, [phone, initCanvas, played]);

  // ─── Reveal (80% scratched) ────────────────────────────────
  const handleReveal = useCallback(async () => {
    if (isRevealedRef.current) return;
    isRevealedRef.current = true;
    setPhase("revealed");

    revealFnRef.current = handleReveal;

    const canvas = canvasRef.current;
    if (canvas) { canvas.style.transition = "opacity 0.5s"; canvas.style.opacity = "0"; canvas.style.pointerEvents = "none"; }

    try {
      const r = await scratchCardService.reveal(gameIdRef.current, phone);
      const today = new Date().toISOString().slice(0, 10);
      const prev = parseInt(localStorage.getItem(`scratch_played_${today}`) || "0", 10);
      localStorage.setItem(`scratch_played_${today}`, String(prev + 1));
      setPlayed(prev + 1 >= depositCount);
      if (r.refId) setRefId(r.refId);
      if (won) { sfxWin(); setConfetti(true); setTimeout(() => setWinModal(true), 800); setTimeout(() => setConfetti(false), 7000); }
      analyticsService.trackEvent("scratch_card_result", { won, prize, refId: r.refId, phone });
    } catch (e: any) {
      console.error("Reveal error:", e);
      if (won) { sfxWin(); setConfetti(true); setTimeout(() => setWinModal(true), 800); setTimeout(() => setConfetti(false), 7000); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, won, prize]);

  const handleRestart = useCallback(() => {
    if (played) { setPhase("idle"); return; }
    setWon(false); setPrize(0); setRefId(""); setWinModal(false); setConfetti(false);
    setError(""); gameIdRef.current = ""; isRevealedRef.current = false;
    setScratchPct(0); canvasReadyRef.current = false; setPhase("idle");
  }, [played]);

  const captureReceipt = useCallback(async () => {
    if (!receiptRef.current) return;
    try {
      const c = await html2canvas(receiptRef.current, { backgroundColor: "#002366", scale: 2, useCORS: true });
      const l = document.createElement("a"); l.download = `recu-grattage-${Date.now()}.png`; l.href = c.toDataURL("image/png"); l.click();
    } catch {}
  }, []);

  const shareReceipt = useCallback(async () => {
    if (!receiptRef.current) return;
    try {
      const c = await html2canvas(receiptRef.current, { backgroundColor: "#002366", scale: 2, useCORS: true });
      const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/png"));
      if (!blob) return;
      if (navigator.share && navigator.canShare({ files: [new File([blob], "recu.png", { type: "image/png" })] })) {
        await navigator.share({ title: "Prix Carte à Gratter", text: `J'ai gagné ${prize.toLocaleString("fr-FR")} FCFA !`, files: [new File([blob], "recu.png", { type: "image/png" })] });
      } else captureReceipt();
    } catch {}
  }, [prize, captureReceipt]);

  // ─── RENDER ────────────────────────────────────────────────────
  return (
    <div className="relative min-h-[100dvh] bg-background text-foreground [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)" }}
      />
      {confetti && <ReactConfetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={400} gravity={0.12} colors={["#FFD700", "#FFA000", "#0052cc", "#1976D2", "#FF6F00"]} />}

      {/* AppBar */}
      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <button type="button" onClick={() => navigate(-1)} aria-label="Retour" className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"><ArrowLeft className="size-4" /></button>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold tracking-tight flex items-center gap-2"><Ticket className="size-4" style={{ color: "#FFD700" }} />Carte à Gratter</h1>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[52ch] px-4 py-5 lg:max-w-6xl lg:px-8">

        {/* ─── Phone Entry ────────────────────────────────────── */}
        {phase === "entering" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center pt-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5" style={{ background: "linear-gradient(135deg, #FFD700, #FFA000)", boxShadow: "0 0 40px rgba(255,215,0,0.3)", animation: "glow 2s ease-in-out infinite" }}>
              <Ticket className="w-10 h-10 text-foreground" />
            </div>
            <h2 className="text-2xl font-extrabold mb-2 text-foreground">Carte à Gratter</h2>
            <p className="text-sm mb-8 text-center text-muted-foreground">Grattez et gagnez jusqu'à 100 000 FCFA !</p>
            <div className="w-full max-w-xs">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-subtle" />
                <input type="tel" inputMode="numeric" placeholder="Numéro Orange (ex: 07XX XX XX)" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSubmitPhone()} className="w-full rounded-2xl border border-border bg-muted py-3 pl-11 pr-4 text-base font-medium text-foreground outline-none placeholder:text-text-subtle" autoFocus />
              </div>
              {error && <p className="text-sm mt-2 text-center text-red-400">{error}</p>}
              <motion.button onClick={handleSubmitPhone} whileTap={{ scale: 0.97 }} disabled={loading} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold text-foreground shadow-lg" style={{ background: "linear-gradient(135deg, #FFD700, #FFA000)", boxShadow: "0 6px 24px rgba(255,215,0,0.35)" }}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Flame className="w-5 h-5" />}
                {loading ? "Vérification..." : "Commencer"}
              </motion.button>
            </div>
            <div className="mt-8 w-full max-w-xs rounded-2xl p-4 bg-card-muted border border-border">
              <div className="flex gap-3">
                <div className="p-2 rounded-xl shrink-0 bg-amber-500/15"><Info className="w-4 h-4 text-amber-400" /></div>
                <div className="text-xs space-y-1 text-text-subtle">
                  <p><strong className="text-foreground">1 essai par dépôt Orange d'au moins 250 FCFA</strong> effectué aujourd'hui.</p>
                  <p>Gagnez et recevez un bon pour récupérer votre lot.</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ─── Game UI ───────────────────────────────────────── */}
        {phase !== "entering" && (
          <>
            {/* Prize badges */}
            <div className="flex justify-center gap-2 mb-4 flex-wrap">
              {[10000, 25000, 50000, 100000].map((p) => (
                <div key={p} className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: "linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,152,0,0.08))", color: "#FFA000", border: "1px solid rgba(255,215,0,0.25)" }}>
                  {p.toLocaleString("fr-FR")} FCFA
                </div>
              ))}
            </div>

            {/* ─── Scratch Card ──────────────────────────────── */}
            <div className="flex justify-center mb-5">
              <div ref={containerRef} className="relative w-full max-w-sm overflow-hidden" style={{
                height: 320,
                borderRadius: 16,
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                border: won
                  ? "3px dashed #DAA520"
                  : "3px dashed #bbb",
              }}>

                {/* ── Card background (cream/off-white, like a real ticket) ── */}
                <div className="absolute inset-0" style={{
                  background: won
                    ? "linear-gradient(180deg, #fffbe6 0%, #fff8e1 30%, #fff3cc 100%)"
                    : "linear-gradient(180deg, #f5f5f5 0%, #eeeeee 30%, #e8e8e8 100%)",
                  padding: 16,
                }}>

                  {/* Corner stars */}
                  {won && (
                    <div className="absolute top-2 left-3 text-lg select-none" style={{ color: "#DAA520" }}>✦</div>
                  )}
                  {won && (
                    <div className="absolute top-2 right-3 text-lg select-none" style={{ color: "#DAA520" }}>✦</div>
                  )}

                  {/* Symbol grid */}
                  <div className="absolute inset-0 flex items-center justify-center pt-6 pb-2">
                    <div className="grid grid-cols-5 gap-x-3 gap-y-2 select-none">
                      {CARD_SYMBOLS.map((sym, i) => (
                        <span key={i} className="text-base text-center" style={{
                          opacity: won ? 0.25 + (i % 3) * 0.08 : 0.08 + (i % 3) * 0.03,
                        }}>{sym}</span>
                      ))}
                    </div>
                  </div>

                  {SPARKLES.map((s) => (
                    <div key={s.id} className="absolute rounded-full" style={{
                      width: s.size, height: s.size, top: `${s.y}%`, left: `${s.x}%`,
                      backgroundColor: won ? "#DAA520" : "#999", opacity: won ? s.opacity * 1.5 : s.opacity,
                    }} />
                  ))}

                  {/* ── THE PRIZE / RESULT underneath ── */}
                  {(phase === "scratching" || phase === "revealed") && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                      {won ? (
                        <>
                          <div className="text-5xl mb-2">🎉</div>
                          <span className="text-sm font-black uppercase tracking-[0.25em] mb-2" style={{ color: "#B8860B" }}>
                            {WIN_MESSAGES[winMsgIdx.current]}
                          </span>
                          <div className="px-5 py-2 rounded-xl" style={{
                            background: "linear-gradient(135deg, rgba(218,165,32,0.12), rgba(218,165,32,0.06))",
                            border: "2px solid rgba(218,165,32,0.35)",
                          }}>
                            <span className="text-3xl font-extrabold" style={{ color: "#B8860B" }}>
                              {prize.toLocaleString("fr-FR")} FCFA
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="flex gap-3 mb-2">
                            {LOSE_EMOJIS.map((em, i) => (
                              <span key={i} className="text-4xl" style={{ opacity: 0.55 }}>{em}</span>
                            ))}
                          </div>
                          <span className="text-sm font-semibold" style={{ color: "#888" }}>
                            {LOSE_LABELS[loseEmojiIdx.current]}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {phase === "idle" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                      <Ticket className="w-14 h-14 mb-2 text-amber-600/30" />
                      <span className="text-gray-400 text-sm">Prêt à gratter ?</span>
                    </div>
                  )}
                </div>

                {/* ── Canvas overlay ── */}
                {phase === "scratching" && (
                  <canvas ref={canvasRef} className="absolute inset-0 z-20" style={{ borderRadius: 13, touchAction: "none", cursor: "crosshair" }}
                    onTouchStart={(e) => { e.preventDefault(); lastPosRef.current = null; const p = getPos(e); scratch(p.x, p.y); }}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseDown={(e) => { lastPosRef.current = null; const p = getPos(e); scratch(p.x, p.y); }}
                    onMouseMove={onMouseMove}
                    onMouseUp={() => { lastPosRef.current = null; }}
                    onMouseLeave={() => { lastPosRef.current = null; }}
                  />
                )}

                {/* ── Progress bar ── */}
                {phase === "scratching" && (
                  <div className="absolute bottom-0 left-0 right-0 z-30 h-1">
                    <div className="h-full transition-all duration-300" style={{
                      width: `${Math.min(scratchPct * 100, 100)}%`,
                      background: won
                        ? "linear-gradient(90deg, #FFD700, #FFA000)"
                        : "linear-gradient(90deg, #aaa, #888)",
                    }} />
                  </div>
                )}

                {/* ── Revealed overlay (win/lose banner) ── */}
                {phase === "revealed" && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute inset-x-0 bottom-0 z-30 py-4 flex items-center justify-center"
                    style={{
                      background: won
                        ? "linear-gradient(180deg, transparent, rgba(255,215,0,0.15))"
                        : "linear-gradient(180deg, transparent, rgba(255,255,255,0.5))",
                    }}
                  >
                    {won ? (
                      <div className="flex items-center gap-2 px-5 py-2.5 rounded-xl" style={{
                        background: "linear-gradient(135deg, #FFD700, #FFA000)",
                        boxShadow: "0 4px 20px rgba(255,215,0,0.4)",
                      }}>
                        <Sparkles className="w-5 h-5 text-foreground" />
                        <span className="text-foreground font-bold text-sm">Vous avez gagné !</span>
                      </div>
                    ) : (
                      <div className="px-5 py-2.5 rounded-xl bg-card border border-gray-200 shadow-sm">
                        <span className="text-gray-500 font-semibold text-sm">{LOSE_LABELS[loseEmojiIdx.current]}</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            </div>

            {/* ─── Buttons ──────────────────────────────────────── */}
            <AnimatePresence mode="wait">
              {phase === "idle" && !played && (
                <motion.button key="play" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} onClick={handlePlay} className="w-full py-4 rounded-2xl text-foreground font-bold text-lg flex items-center justify-center gap-2 shadow-lg active:scale-[0.97] transition-transform" style={{ background: "linear-gradient(135deg, #FFD700, #FFA000)", boxShadow: "0 6px 24px rgba(255,215,0,0.35)" }} disabled={loading}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Flame className="w-5 h-5" />}
                  {loading ? "Chargement..." : "Obtenir une Carte"}
                </motion.button>
              )}

              {error && <motion.p key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-sm font-medium mt-3 text-red-400">{error}</motion.p>}

              {phase === "idle" && played && (
                <motion.div key="locked" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="w-full py-4 rounded-2xl flex items-center justify-center gap-2 bg-card-muted border border-border">
                  <Lock className="w-5 h-5 text-text-subtle" />
                  <span className="font-semibold text-text-subtle">Essai épuisé — Rechargez pour rejouer !</span>
                </motion.div>
              )}

              {phase === "scratching" && (
                <motion.div key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center">
                  <div className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-card-muted border border-border">
                    <span className="text-2xl animate-bounce">👆</span>
                    <span className="font-bold text-lg text-amber-400">Grattez la carte !</span>
                  </div>
                </motion.div>
              )}

              {phase === "revealed" && (
                <motion.button key="restart" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} onClick={handleRestart} className="w-full py-4 rounded-2xl text-foreground font-bold text-lg flex items-center justify-center gap-2 shadow-lg active:scale-[0.97] transition-transform" style={{ background: "linear-gradient(135deg, #0052cc, #00A045)", boxShadow: "0 6px 24px rgba(0,200,83,0.3)" }}>
                  <Gift className="w-5 h-5" /> Rejouer
                </motion.button>
              )}
            </AnimatePresence>

            {/* Info */}
            <div className="mt-5 rounded-2xl p-4 bg-card-muted border border-border">
              <div className="flex gap-3">
                <div className="p-2 rounded-xl shrink-0 bg-amber-500/15"><Info className="w-4 h-4 text-amber-400" /></div>
                <div className="text-xs space-y-1.5 text-text-subtle">
                  <p><strong className="text-foreground">Comment jouer :</strong> Grattez la surface argentée avec votre doigt pour découvrir votre prix.</p>
                  <p><strong className="text-foreground">1 essai par dépôt Orange ≥ 250 FCFA</strong> — Gagnez et recevez un bon pour récupérer votre lot.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Win Modal ──────────────────────────────────────────── */}
      <Dialog open={winModal} onOpenChange={setWinModal}>
        <DialogContent className="sm:max-w-md" style={{ backgroundColor: "#1a1a2e", borderRadius: 20, border: "1px solid rgba(255,215,0,0.2)" }}>
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-extrabold flex items-center justify-center gap-2 text-amber-400">
              <Sparkles className="w-6 h-6" /> {WIN_MESSAGES[winMsgIdx.current]}
            </DialogTitle>
            <DialogDescription className="text-center text-text-subtle">Téléchargez votre reçu et contactez l'assistance pour réclamer votre lot.</DialogDescription>
          </DialogHeader>
          <div ref={receiptRef} className="rounded-2xl p-5 text-center" style={{ background: "linear-gradient(145deg, #002366, #0052cc)", border: "2px solid rgba(255,215,0,0.5)" }}>
            <div className="flex items-center justify-center gap-2 mb-3"><Ticket className="w-7 h-7 text-amber-400" /><span className="text-foreground font-bold">Carte à Gratter</span></div>
            <div className="text-3xl font-extrabold mb-1 text-amber-400">{prize.toLocaleString("fr-FR")} FCFA</div>
            <div className="border-t border-border pt-3 mt-3 space-y-1.5">
              <div className="flex justify-between text-xs text-text-subtle"><span>Tél: {phone}</span><span>{new Date().toLocaleDateString("fr-FR")}</span></div>
              <div className="flex justify-between text-xs text-text-subtle"><span>Ref: {refId}</span><span>{new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span></div>
            </div>
            <div className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">Preuve de gain — Envoyez à l'assistance pour récupérer votre gain</div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col pt-2">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={captureReceipt} className="py-3 rounded-xl text-foreground font-bold text-sm flex items-center justify-center gap-2 active:scale-95" style={{ background: "linear-gradient(135deg, #1976D2, #1565C0)" }}><Download className="w-4 h-4" /> Télécharger</button>
              <button onClick={shareReceipt} className="py-3 rounded-xl text-foreground font-bold text-sm flex items-center justify-center gap-2 active:scale-95" style={{ background: "linear-gradient(135deg, #0052cc, #00A045)" }}><Share2 className="w-4 h-4" /> Partager</button>
            </div>
            <button onClick={() => { const m = encodeURIComponent(`Bonjour, j'ai gagné ${prize.toLocaleString("fr-FR")} FCFA au jeu "Carte à Gratter".\n\nTél: ${phone}\nRéf: ${refId}`); window.open(`https://wa.me/?text=${m}`, "_blank"); }} className="w-full py-3.5 rounded-xl text-foreground font-bold text-base flex items-center justify-center gap-2 shadow-lg active:scale-[0.97]" style={{ background: "#25D366", boxShadow: "0 4px 16px rgba(37,211,102,0.3)" }}>
              <Gift className="w-5 h-5" /> Envoyer sur WhatsApp
            </button>
            <button type="button" onClick={() => setWinModal(false)} className="w-full rounded-xl py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground">Plus tard</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ScratchCardPage;
